import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

// Les notions de français que des leçons scannées enseignent et que l'arbre n'a pas
// (PLAN_IMPL_SCAN_LECON §2.6). Agrégé : ni enfant, ni texte de leçon — le libellé tel que la
// leçon nomme la notion, des compteurs, des dates. Pilote l'ajout de notions à l'arbre.

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('fr-FR'); } catch { return String(v); }
}

function cycles(scansParCycle) {
  const entrees = Object.entries(scansParCycle || {}).filter(([c]) => c !== '0');
  if (!entrees.length) return '—';
  return entrees.sort(([a], [b]) => Number(a) - Number(b)).map(([c, n]) => `C${c} × ${n}`).join(' · ');
}

function tauxSignales(n) {
  if (!n.nbQuestionsRetenues) return null;
  return Math.round((n.nbQuestionsSignalees / n.nbQuestionsRetenues) * 100);
}

export default function NotionsDemandeesScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tri, setTri] = useState('scans');
  const [inclureAjoutees, setInclureAjoutees] = useState(false);
  const [notions, setNotions] = useState([]);
  const [enCours, setEnCours] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.getNotionsDemandees({ tri, inclureAjoutees });
      setNotions(Array.isArray(data?.notions) ? data.notions : []);
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement des notions demandées');
    } finally {
      setLoading(false);
    }
  }, [tri, inclureAjoutees]);

  useEffect(() => { charger(); }, [charger]);

  const basculer = async (notion) => {
    setEnCours(notion.id);
    try {
      await AdminService.marquerNotionDemandeeAjoutee(notion.id, !notion.ajouteeArbre);
      await charger();
    } catch (e) {
      setError(e?.message || 'Mise à jour impossible');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Notions demandées</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
          Les notions de français que les enfants scannent et que l'arbre n'a pas. Chacune reçoit
          aujourd'hui des QCM écrits par une IA (gardés : citation de la leçon, élève à l'aveugle) ;
          les plus demandées sont à ajouter à l'arbre, avec leurs générateurs. Rien ici ne désigne un
          enfant ni ne reprend le texte d'une leçon.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={tri} onChange={(_e, v) => { if (v) setTri(v); }}>
            <ToggleButton value="scans">Les plus scannées</ToggleButton>
            <ToggleButton value="recent">Les plus récentes</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={<Switch checked={inclureAjoutees} onChange={(e) => setInclureAjoutees(e.target.checked)} />}
            label="Montrer celles déjà ajoutées à l'arbre"
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : notions.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary">
              Aucune notion demandée pour l'instant : toutes les leçons scannées portaient sur des notions de l'arbre.
            </Typography>
          </CardContent></Card>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Notion (telle que la leçon la nomme)</TableCell>
                  <TableCell>Domaine / compétence probables</TableCell>
                  <TableCell>Cycles</TableCell>
                  <TableCell align="right">Scans</TableCell>
                  <TableCell>Premier · dernier</TableCell>
                  <TableCell align="right">QCM générés · replis</TableCell>
                  <TableCell align="right">Questions signalées</TableCell>
                  <TableCell>Dans l'arbre</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notions.map((n) => {
                  const taux = tauxSignales(n);
                  return (
                    <TableRow key={n.id} hover>
                      <TableCell><strong>{n.libelle}</strong></TableCell>
                      <TableCell>
                        {n.domaine || '—'}
                        {n.competence ? (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            / {n.competence}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{cycles(n.scansParCycle)}</TableCell>
                      <TableCell align="right"><strong>{n.nbScans}</strong></TableCell>
                      <TableCell>{fmtDate(n.premierScan)} · {fmtDate(n.dernierScan)}</TableCell>
                      <TableCell align="right">{n.nbQuizGeneres} · {n.nbQuizReplis}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={`${n.nbQuestionsSignalees} signalée(s) sur ${n.nbQuestionsRetenues} question(s) retenue(s)`}>
                          <span>
                            {taux === null ? '—' : (
                              <Chip size="small" color={taux >= 10 ? 'error' : taux > 0 ? 'warning' : 'default'} label={`${taux} %`} />
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={n.ajouteeArbre ? 'outlined' : 'contained'}
                          disabled={enCours === n.id}
                          onClick={() => basculer(n)}
                        >
                          {n.ajouteeArbre ? `Ajoutée le ${fmtDate(n.ajouteeLe)} — annuler` : 'Ajoutée à l’arbre'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </AdminLayout>
  );
}
